import { ColumnDef, SortingFn } from '@tanstack/react-table';
import * as E from './editing/editors';
import type { TChoicesKind } from './ColumnUtils';
import { formatCustomNumber } from 'src/components/CustomNumberInput';

export type CustomFieldDef = {
  id: string;
  name: string;
  kind: 'text' | 'number' | 'choice' | 'list' | 'boolean';
  default: any;
  choices?: string[];
  precision?: number;
  min?: number | null;
  max?: number | null;
};

export function formatCustomFieldValue(
  field: CustomFieldDef,
  value: any
): string {
  const resolved = value !== undefined ? value : field.default;

  if (field.kind === 'boolean') {
    return resolved ? 'Yes' : 'No';
  }

  if (field.kind === 'list') {
    let items: any[] = [];
    if (Array.isArray(resolved)) {
      items = resolved;
    } else if (typeof resolved === 'string' && resolved.trim()) {
      try {
        const parsed = JSON.parse(resolved);
        items = Array.isArray(parsed) ? parsed : [resolved];
      } catch {
        items = resolved
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }
    } else if (resolved != null && resolved !== '') {
      items = [resolved];
    }
    return items.map(String).filter(Boolean).join(', ');
  }

  if (field.kind === 'number' && resolved != null && resolved !== '') {
    return formatCustomNumber(resolved, field.precision ?? 0);
  }

  if (resolved == null) return '';
  return String(resolved);
}

type BuildOptions = {
  /**
   * When set, read values from row[endSide].custom_fields instead of
   * row.properties.custom_fields, and prefix column ids/headers.
   */
  endSide?: 'local' | 'remote';
  /** Bulk-edit API kind used by CellEditor (ptp, site, access_point, subscriber). */
  choicesKind?: TChoicesKind;
  /** Aggregation for grouped PTP rows. */
  aggregationFn?: ColumnDef<any>['aggregationFn'];
};

function editorForField(field: CustomFieldDef) {
  switch (field.kind) {
    case 'number':
      return E.NumberEditor;
    case 'choice':
    case 'list':
      return E.DropdownEditor;
    case 'boolean':
      return E.BooleanEditor;
    case 'text':
    default:
      return E.TextEditor;
  }
}

// Minimum width a custom field column can be resized down to. Matches the
// `.lp-table > tbody > tr > td { min-width: 80px }` rule in App.css, so a
// resized custom field column never ends up narrower than any other cell.
const MIN_CUSTOM_FIELD_COLUMN_WIDTH = 80;

// Initial width for a newly-added custom field column, before the user
// resizes it.
function defaultSizeForField(field: CustomFieldDef): number {
  switch (field.kind) {
    case 'boolean':
      return MIN_CUSTOM_FIELD_COLUMN_WIDTH;
    case 'number':
      return MIN_CUSTOM_FIELD_COLUMN_WIDTH / 2 + 15 * (field.precision ?? 0);
    case 'choice':
      return MIN_CUSTOM_FIELD_COLUMN_WIDTH + 20;
    case 'list':
      return MIN_CUSTOM_FIELD_COLUMN_WIDTH + 30;
    case 'text':
    default:
      return MIN_CUSTOM_FIELD_COLUMN_WIDTH;
  }
}

/** Underlying number of a number field, or null when blank/unparseable. */
function numericFieldValue(field: CustomFieldDef, value: any): number | null {
  const resolved = value !== undefined ? value : field.default;
  if (resolved == null || resolved === '') return null;
  const num = Number(resolved);
  return Number.isFinite(num) ? num : null;
}

function sortingFnForField(field: CustomFieldDef): SortingFn<any> {
  if (field.kind === 'number') {
    // Sort on the numbers themselves. Comparing the formatted strings orders
    // them as text, which puts 10 before 9 and mis-places negatives.
    return (rowA, rowB, columnId) => {
      const a = numericFieldValue(field, rowA.getValue(columnId));
      const b = numericFieldValue(field, rowB.getValue(columnId));
      if (a === null && b === null) return 0;
      // Blanks group together at one end rather than sorting as 0
      if (a === null) return 1;
      if (b === null) return -1;
      return a - b;
    };
  }
  return (rowA, rowB, columnId) => {
    const a = formatCustomFieldValue(field, rowA.getValue(columnId));
    const b = formatCustomFieldValue(field, rowB.getValue(columnId));
    return a.localeCompare(b, undefined, {
      sensitivity: 'base',
      numeric: true,
    });
  };
}

/**
 * Given a list of custom field definitions for a category,
 * return TanStack Table ColumnDef entries that can be spread
 * into any table's column list.
 *
 * Column IDs use the prefix "cf_" to avoid collisions with
 * static column IDs. PTP end fields use "cf_local_" / "cf_remote_".
 */
export function buildCustomFieldColumns<T extends Record<string, any>>(
  fields: CustomFieldDef[],
  options: BuildOptions = {}
): ColumnDef<T>[] {
  const { endSide, choicesKind, aggregationFn } = options;

  return (fields || []).map((field) => {
    const id = endSide ? `cf_${endSide}_${field.id}` : `cf_${field.id}`;
    const header = endSide
      ? `${field.name} (${endSide === 'local' ? 'Local' : 'Remote'})`
      : field.name;

    const valueAttr = endSide
      ? `${endSide}.custom_fields.${field.id}`
      : `properties.custom_fields.${field.id}`;

    const meta: Record<string, any> = {
      isCustomField: true,
      fieldDef: field,
      endSide: endSide ?? null,
      csv: ({ getValue }: any) => formatCustomFieldValue(field, getValue()),
    };

    if (choicesKind) {
      meta.editable = true;
      meta.choicesKind = choicesKind;
      meta.attr = valueAttr;
      meta.columnId = id;
      meta.editorLabel = header;
      meta.editor = editorForField(field);
      meta.kind =
        field.kind === 'number'
          ? 'number'
          : field.kind === 'boolean'
            ? 'boolean'
            : 'string';

      if (field.kind === 'number') {
        meta.editProps = {
          precision: field.precision ?? 0,
          min: field.min ?? undefined,
          max: field.max ?? undefined,
          // Snap entered values to the field's decimal precision on commit
          roundToPrecision: true,
        };
      }

      if (field.kind === 'choice' || field.kind === 'list') {
        const choices = field.choices || [];
        meta.getChoices = async () => ({ choices });
        meta.multiple = field.kind === 'list';
      }

      if (field.kind === 'boolean') {
        meta.boolOptions = ['Yes', 'No'];
      }
    }

    return {
      id,
      header,
      size: defaultSizeForField(field),
      minSize: MIN_CUSTOM_FIELD_COLUMN_WIDTH,
      enableSorting: true,
      enableGlobalFilter: true,
      ...(aggregationFn ? { aggregationFn } : {}),
      enableColumnFilter: true,
      sortingFn: sortingFnForField(field),
      accessorFn: (row: T): any => {
        const customFields = endSide
          ? row?.[endSide]?.custom_fields
          : row?.properties?.custom_fields;
        const value = customFields?.[field.id];
        return value !== undefined ? value : field.default;
      },
      cell: ({ getValue }) => formatCustomFieldValue(field, getValue()),
      meta,
    };
  });
}
